import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RefreshCw, Receipt, AlertTriangle, Scale, Clock,
  FileDown, Layers, SlidersHorizontal,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Table } from '../../components/ui/Table';
import { syncTimestampColumns } from '../../components/ui/SyncTimestamps';
import { Badge } from '../../components/ui/Badge';
import { FilterBar } from '../../components/ui/FilterBar';
import { KpiCard } from '../../components/ui/KpiCard';
import api from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import {
  PER_PAGE, EMPTY_ADV, STATUS_KEYS, fmtDate, fmtAmount,
} from './utils';
import { InvoiceFilterModal } from './components/InvoiceFilterModal';
import { AgedBalanceCard }    from './components/AgedBalanceCard';
import { TopDebiteursCard }   from './components/TopDebiteursCard';
import { InvoiceEmptyState }  from './components/InvoiceEmptyState';
import { InvoiceDrawer }      from './components/InvoiceDrawer';

export default function InvoicesPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { user, hasPermission, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !hasPermission('view_invoices')) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, user, hasPermission, navigate]);

  const [rows,  setRows]  = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loadingRows,  setLoadingRows]  = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [error, setError] = useState(null);

  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page,   setPage]   = useState(1);
  const debouncedSearch = useDebounce(search, 400);

  const [advFilters, setAdvFilters] = useState(EMPTY_ADV);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  function loadStats() {
    setLoadingStats(true);
    api.get('/invoices/stats')
      .then((r) => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoadingStats(false));
  }

  const loadRows = useCallback(() => {
    setLoadingRows(true);
    setError(null);
    const params = new URLSearchParams();
    if (advFilters.statuses.length > 0) {
      params.set('statuses', advFilters.statuses.join(','));
    } else if (filter !== 'all') {
      params.set('status', filter);
    }
    if (debouncedSearch.trim())  params.set('search',    debouncedSearch.trim());
    if (advFilters.minAmount)    params.set('minAmount', advFilters.minAmount);
    if (advFilters.maxAmount)    params.set('maxAmount', advFilters.maxAmount);
    if (advFilters.minDelay)     params.set('minDelay',  advFilters.minDelay);
    if (advFilters.maxDelay)     params.set('maxDelay',  advFilters.maxDelay);
    if (advFilters.dateFrom)     params.set('dateFrom',  advFilters.dateFrom);
    if (advFilters.dateTo)       params.set('dateTo',    advFilters.dateTo);
    params.set('page',  String(page));
    params.set('limit', String(PER_PAGE));
    api.get(`/invoices?${params}`)
      .then((r) => {
        setRows(r.data.rows ?? []);
        setTotal(r.data.total ?? 0);
      })
      .catch((e) => setError(e.message || t('common.error_occurred')))
      .finally(() => setLoadingRows(false));
  }, [filter, advFilters, debouncedSearch, page, t]);

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadRows(); }, [loadRows]);

  const prevFilter     = useRef(filter);
  const prevSearch     = useRef(debouncedSearch);
  const prevAdvFilters = useRef(advFilters);
  useEffect(() => {
    if (
      filter !== prevFilter.current ||
      debouncedSearch !== prevSearch.current ||
      advFilters !== prevAdvFilters.current
    ) {
      prevFilter.current     = filter;
      prevSearch.current     = debouncedSearch;
      prevAdvFilters.current = advFilters;
      setPage(1);
    }
  }, [filter, debouncedSearch, advFilters]);

  function refresh() { loadStats(); loadRows(); }

  const enriched = useMemo(() => rows.map((inv) => {
    const mapped = STATUS_KEYS[inv.status] || { variant: 'info', key: null, group: 'other' };
    return {
      _id:              inv._id,
      reference:        inv.reference || '—',
      shipToName:       inv.shipToName || inv.billToCustomerNo || '—',
      shipToCode:       inv.shipToCode || inv.finalDestinationCode || '—',
      issuedAt:         fmtDate(inv.issuedAt, locale),
      dueDate:          fmtDate(inv.dueDate, locale),
      statusVariant:    mapped.variant,
      statusKey:        mapped.key,
      statusGroup:      mapped.group,
      ageDays:          inv.ageDays || 0,
      amountTTC:        inv.amountTTC ?? null,
      amountEur:        inv.amountEur ?? null,
      vatAmount:        inv.vatAmount ?? null,
      currency:         inv.fretCurrencyCode || 'EUR',
      billToCustomerNo: inv.billToCustomerNo || null,
      finalDestination: inv.finalDestinationCode || null,
      pdfUrl:           inv.pdfUrl || null,
      disputeNote:      inv.disputeNote || null,
      lineItems:        inv.lineItems || [],
      createdAt:        inv.createdAt,
      updatedAt:        inv.updatedAt,
    };
  }), [rows, locale]);

  const columns = useMemo(() => [
    {
      key: 'reference',
      label: t('invoices.col_number'),
      render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
    },
    {
      key: 'shipToName',
      label: t('invoices.col_client'),
      render: (v) => <span className="font-medium text-slate-700">{v || '—'}</span>,
    },
    {
      key: 'shipToCode',
      label: t('invoices.col_shipment'),
      render: (v) => <span className="font-mono text-[12px] text-slate-500">{v || '—'}</span>,
    },
    {
      key: 'issuedAt',
      label: t('invoices.col_issued'),
      render: (v) => <span className="text-slate-600">{v}</span>,
    },
    {
      key: 'dueDate',
      label: t('invoices.col_due'),
      render: (v) => <span className="text-slate-600">{v}</span>,
    },
    {
      key: 'statusVariant',
      label: t('invoices.col_status'),
      render: (v, row) => (
        <Badge variant={v}>{row.statusKey ? t(`invoices.${row.statusKey}`) : '—'}</Badge>
      ),
    },
    {
      key: 'ageDays',
      label: t('invoices.col_delay'),
      align: 'right',
      render: (v, row) => {
        if (!v || row.statusGroup !== 'overdue') return <span className="text-slate-400">—</span>;
        return (
          <span className={`tabular-nums font-semibold text-[12px] ${
            row.statusVariant === 'overdue60' ? 'text-rose-600' : 'text-orange-600'
          }`}>
            +{v} j
          </span>
        );
      },
    },
    {
      key: 'amountTTC',
      label: t('invoices.col_amount'),
      align: 'right',
      render: (v, row) => (
        <span className="tabular-nums font-semibold text-slate-800 text-[12.5px]">
          {fmtAmount(v ?? row.amountEur, row.currency, locale)}
        </span>
      ),
    },
    ...syncTimestampColumns(t, locale),
  ], [t, locale]);

  const activeAdvCount = [
    advFilters.statuses.length > 0,
    advFilters.minAmount !== '' || advFilters.maxAmount !== '',
    advFilters.minDelay  !== '' || advFilters.maxDelay  !== '',
    advFilters.dateFrom  !== '' || advFilters.dateTo    !== '',
  ].filter(Boolean).length;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">{t('invoices.title')}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{t('invoices.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loadingRows}
            className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition"
          >
            <RefreshCw size={13} strokeWidth={1.75} className={loadingRows ? 'animate-spin' : ''} />
            {t('common.refresh')}
          </button>
          <button className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 transition">
            <FileDown size={13} strokeWidth={1.75} />
            {t('invoices.btn_export')}
          </button>
          <button className="h-8 px-3 flex items-center gap-1.5 rounded-lg bg-primary text-white text-[12px] font-semibold hover:opacity-90 transition">
            <Layers size={13} strokeWidth={1.75} />
            {t('invoices.btn_batch_pay')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-center justify-between text-[13px] text-rose-700">
          <span>{error}</span>
          <button onClick={refresh} className="font-medium underline text-[12px] text-rose-700">
            {t('common.retry')}
          </button>
        </div>
      )}

      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label={t('invoices.kpi_encours')}
          value={loadingStats ? '—' : fmtAmount(stats?.encours?.amount, 'EUR', locale)}
          sub={!loadingStats ? t('invoices.kpi_encours_sub', { count: stats?.encours?.count ?? 0 }) : undefined}
          icon={Receipt}
        />
        <KpiCard
          label={t('invoices.kpi_echu')}
          value={loadingStats ? '—' : fmtAmount(stats?.echu?.amount, 'EUR', locale)}
          sub={!loadingStats && (stats?.echu?.count ?? 0) > 0 ? t('invoices.kpi_echu_sub') : undefined}
          trend={!loadingStats && (stats?.echu?.count ?? 0) > 0 ? 'down' : undefined}
          trendLabel={!loadingStats && (stats?.echu?.count ?? 0) > 0 ? t('invoices.kpi_echu_sub') : undefined}
          icon={AlertTriangle}
        />
        <KpiCard
          label={t('invoices.kpi_contested')}
          value={loadingStats ? '—' : fmtAmount(stats?.conteste?.amount, 'EUR', locale)}
          sub={!loadingStats ? t('invoices.kpi_contested_sub', { count: stats?.conteste?.count ?? 0 }) : undefined}
          icon={Scale}
        />
        <KpiCard
          label={t('invoices.kpi_regle_ytd')}
          value={loadingStats ? '—' : fmtAmount(stats?.regleYTD?.amount, 'EUR', locale)}
          trend={!loadingStats && stats?.regleYTD?.pctVsPrev != null
            ? (stats.regleYTD.pctVsPrev >= 0 ? 'up' : 'down') : undefined}
          trendLabel={!loadingStats && stats?.regleYTD?.pctVsPrev != null
            ? `${stats.regleYTD.pctVsPrev >= 0 ? '+' : ''}${stats.regleYTD.pctVsPrev} % vs N-1`
            : undefined}
          icon={Clock}
        />
      </div>

      {!loadingStats && stats && (
        <div className="grid grid-cols-[1fr_320px] gap-4">
          <AgedBalanceCard stats={stats} locale={locale} t={t} />
          <TopDebiteursCard stats={stats} locale={locale} t={t} />
        </div>
      )}
      {loadingStats && (
        <div className="grid grid-cols-[1fr_320px] gap-4">
          <div className="h-[168px] bg-white border border-slate-200 rounded-xl animate-pulse" />
          <div className="h-[168px] bg-white border border-slate-200 rounded-xl animate-pulse" />
        </div>
      )}

      <FilterBar
        groups={[{
          value: filter,
          onChange: (key) => setFilter(key),
          tabs: [
            { key: 'all',      label: t('invoices.filter_all')      },
            { key: 'pending',  label: t('invoices.filter_pending')  },
            { key: 'paid',     label: t('invoices.filter_paid')     },
            { key: 'overdue',  label: t('invoices.filter_overdue')  },
            { key: 'disputed', label: t('invoices.filter_disputed') },
          ],
        }]}
        search={search}
        onSearch={(e) => setSearch(e.target.value)}
        searchPlaceholder={t('invoices.search_placeholder')}
        actions={
          <button
            onClick={() => setFilterModalOpen(true)}
            className={`h-9 px-3 flex items-center gap-1.5 rounded-lg border text-[12px] font-medium transition ${
              activeAdvCount > 0
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <SlidersHorizontal size={13} strokeWidth={1.75} />
            {t('invoices.adv_filter_btn')}
            {activeAdvCount > 0 && (
              <span className="flex items-center justify-center w-[18px] h-[18px] rounded-full bg-primary text-white text-[10px] font-bold">
                {activeAdvCount}
              </span>
            )}
          </button>
        }
      />

      <InvoiceFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        value={advFilters}
        emptyValue={EMPTY_ADV}
        onApply={(v) => setAdvFilters(v)}
      />

      <Table
        columns={columns}
        rows={enriched}
        onRowClick={setSelectedRow}
        loading={loadingRows}
        emptyState={<InvoiceEmptyState />}
        page={page}
        total={total}
        perPage={PER_PAGE}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <InvoiceDrawer row={selectedRow} onClose={() => setSelectedRow(null)} locale={locale} />
    </div>
  );
}
